import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Copy, Download, CheckCircle2, Loader2, KeyRound } from 'lucide-react';
import { authApi } from '@/lib/api';
import { useAuthStore } from '@/store/auth.store';
import AppLogo from '@/components/AppLogo';

type Step = 'scan' | 'verify' | 'codes';

export default function MfaSetupPage() {
  const navigate    = useNavigate();
  const { setAuth } = useAuthStore();

  // Snapshot tempKey once at mount — do NOT read reactively from the store.
  // If we used the store value as an effect dependency, calling setAuth()
  // (which clears mfaSetupTempKey to null) would re-trigger the effect and
  // immediately redirect to /login, overriding handleDone's navigation.
  const [tempKey]   = useState(() => useAuthStore.getState().mfaSetupTempKey);

  const [step, setStep]                 = useState<Step>('scan');
  const [qrDataUrl, setQrDataUrl]       = useState('');
  const [secret, setSecret]             = useState('');
  const [token, setToken]               = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [copied, setCopied]             = useState(false);
  const [error, setError]               = useState<string | null>(null);
  const [loading, setLoading]           = useState(false);
  const [pendingUser, setPendingUser]   = useState<any>(null);
  const [pendingTokens, setPendingTokens] = useState<{ access: string; refresh: string } | null>(null);

  // Load QR code once on mount using the snapshotted tempKey
  useEffect(() => {
    if (!tempKey) { navigate('/login', { replace: true }); return; }
    (async () => {
      setLoading(true);
      try {
        const { data } = await authApi.mfaSetupInit(tempKey);
        setQrDataUrl(data.qrDataUrl);
        setSecret(data.secret);
      } catch {
        setError('Setup session expired. Please log in again.');
      } finally {
        setLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally empty — tempKey is captured in closure above

  async function handleVerify() {
    if (token.length !== 6 || !/^\d+$/.test(token)) {
      setError('Enter the 6-digit code from your authenticator app');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const { data } = await authApi.mfaSetupConfirm(tempKey!, token);
      setRecoveryCodes(data.recoveryCodes);
      setPendingUser(data.user);
      setPendingTokens({ access: data.accessToken, refresh: data.refreshToken });
      setStep('codes');
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Verification failed. Try again.');
    } finally {
      setLoading(false);
    }
  }

  function handleDone() {
    if (!pendingUser || !pendingTokens) return;
    setAuth(pendingUser, pendingTokens.access, pendingTokens.refresh);
    navigate('/', { replace: true });
  }

  function copyAll() {
    navigator.clipboard.writeText(recoveryCodes.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function downloadCodes() {
    const blob = new Blob(
      [`Aarogya Saarthi — MFA Recovery Codes\nGenerated: ${new Date().toLocaleString()}\n\n${recoveryCodes.join('\n')}\n\nEach code can only be used once. Store these in a safe place.`],
      { type: 'text/plain' }
    );
    const url = URL.createObjectURL(blob);
    const a   = document.createElement('a');
    a.href    = url;
    a.download = 'aarogya-saarthi-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  }

  const STEPS = [
    { key: 'scan',   label: 'Scan QR' },
    { key: 'verify', label: 'Verify' },
    { key: 'codes',  label: 'Save Codes' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">

      {/* Header */}
      <header className="h-11 bg-navy flex items-center px-6 gap-3">
        <div className="w-6 h-6 rounded-md bg-white/10 flex items-center justify-center">
          <AppLogo size={15} inverted/>
        </div>
        <span className="text-white font-semibold text-sm">Aarogya Saarthi</span>
        <span className="text-white/20">|</span>
        <span className="text-white/40 text-xs">Two-Factor Authentication Setup</span>
      </header>

      {/* Stepper */}
      <div className="bg-white border-b border-gray-200 px-8 py-4">
        <div className="max-w-lg mx-auto flex items-start">
          {STEPS.map((s, i) => {
            const done    = STEPS.indexOf(STEPS.find(x => x.key === step)!) > i;
            const current = s.key === step;
            return (
              <div key={s.key} className="flex items-start flex-1">
                <div className="flex flex-col items-center" style={{ minWidth: 56 }}>
                  <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center
                                   text-sm font-bold transition-colors ${
                    done    ? 'bg-clinical-success border-clinical-success text-white' :
                    current ? 'bg-orange border-orange text-navy' :
                              'bg-white border-gray-200 text-gray-300'}`}>
                    {done ? <CheckCircle2 size={16}/> : i + 1}
                  </div>
                  <span className={`text-xs mt-1 font-medium ${
                    current ? 'text-navy' : done ? 'text-clinical-success' : 'text-gray-300'}`}>
                    {s.label}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 mt-4 mx-1 h-0.5 rounded-full ${done ? 'bg-clinical-success' : 'bg-gray-200'}`}/>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-card-md p-8">

          {/* Step 1 — Scan QR */}
          {step === 'scan' && (
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-orange/10 flex items-center justify-center">
                  <ShieldCheck size={20} className="text-orange"/>
                </div>
                <div>
                  <h2 className="text-navy text-xl font-semibold">Set up authenticator</h2>
                  <p className="text-gray-400 text-sm">Your role requires two-factor authentication</p>
                </div>
              </div>

              <ol className="text-sm text-gray-600 space-y-2 mb-6 list-decimal list-inside">
                <li>Install <strong>Google Authenticator</strong> or <strong>Authy</strong> on your phone</li>
                <li>Open the app and tap <strong>+ Add account</strong></li>
                <li>Scan the QR code below</li>
              </ol>

              {loading ? (
                <div className="flex justify-center py-10">
                  <Loader2 size={32} className="animate-spin text-orange"/>
                </div>
              ) : qrDataUrl ? (
                <div className="flex flex-col items-center gap-4">
                  <img src={qrDataUrl} alt="TOTP QR Code"
                    className="w-48 h-48 rounded-xl border border-gray-100 p-2"/>
                  <div className="w-full bg-gray-50 rounded-lg px-4 py-3 text-center">
                    <p className="text-xs text-gray-400 mb-1">Or enter this key manually:</p>
                    <code className="text-xs font-mono text-navy break-all">{secret}</code>
                  </div>
                </div>
              ) : (
                <div className="text-clinical-danger text-sm text-center">{error}</div>
              )}

              {error && qrDataUrl && (
                <p className="text-clinical-danger text-xs text-center mt-3">{error}</p>
              )}

              <button
                onClick={() => { setError(null); setStep('verify'); }}
                disabled={!qrDataUrl}
                className="mt-6 w-full py-2.5 rounded-lg bg-orange text-navy font-semibold text-sm
                           hover:bg-orange-dark transition-colors disabled:opacity-40">
                I've scanned the QR code →
              </button>
            </>
          )}

          {/* Step 2 — Verify */}
          {step === 'verify' && (
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-orange/10 flex items-center justify-center">
                  <KeyRound size={20} className="text-orange"/>
                </div>
                <div>
                  <h2 className="text-navy text-xl font-semibold">Verify your code</h2>
                  <p className="text-gray-400 text-sm">Enter the 6-digit code from the app</p>
                </div>
              </div>

              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={token}
                onChange={e => setToken(e.target.value.replace(/\D/g, ''))}
                onKeyDown={e => { if (e.key === 'Enter' && token.length === 6 && !loading) handleVerify(); }}
                placeholder="000000"
                autoFocus
                className="w-full px-4 py-3 rounded-lg border border-gray-200 text-center
                           text-2xl font-mono tracking-[0.5em]
                           focus:outline-none focus:ring-2 focus:ring-orange/30 focus:border-orange"
              />

              {error && (
                <div className="mt-3 bg-red-50 border border-red-200 text-clinical-danger
                                text-sm rounded-lg px-4 py-3 text-center">
                  {error}
                </div>
              )}

              <button
                onClick={handleVerify}
                disabled={loading || token.length !== 6}
                className="mt-4 w-full py-2.5 rounded-lg bg-orange text-navy font-semibold text-sm
                           hover:bg-orange-dark transition-colors disabled:opacity-40
                           flex items-center justify-center gap-2">
                {loading ? <Loader2 size={16} className="animate-spin"/> : null}
                {loading ? 'Verifying…' : 'Verify & activate'}
              </button>

              <button onClick={() => setStep('scan')}
                className="mt-3 w-full py-2 text-sm text-gray-400 hover:text-navy transition-colors">
                ← Back to QR code
              </button>
            </>
          )}

          {/* Step 3 — Recovery codes */}
          {step === 'codes' && (
            <>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-clinical-success/10 flex items-center justify-center">
                  <CheckCircle2 size={20} className="text-clinical-success"/>
                </div>
                <div>
                  <h2 className="text-navy text-xl font-semibold">Save your recovery codes</h2>
                  <p className="text-gray-400 text-sm">MFA activated — store these somewhere safe</p>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs
                              text-amber-700 mb-4">
                These codes are shown <strong>only once</strong>. Each can be used once if you lose
                access to your authenticator. Store them in a password manager or print them.
              </div>

              <div className="grid grid-cols-2 gap-2 mb-4">
                {recoveryCodes.map((code, i) => (
                  <div key={i}
                    className="bg-gray-50 rounded-md px-3 py-2 font-mono text-xs text-navy
                               text-center border border-gray-100">
                    {code}
                  </div>
                ))}
              </div>

              <div className="flex gap-2 mb-6">
                <button onClick={copyAll}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg
                             border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                  {copied ? <CheckCircle2 size={14} className="text-clinical-success"/> : <Copy size={14}/>}
                  {copied ? 'Copied!' : 'Copy all'}
                </button>
                <button onClick={downloadCodes}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg
                             border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                  <Download size={14}/>
                  Download
                </button>
              </div>

              <button
                onClick={handleDone}
                className="w-full py-2.5 rounded-lg bg-orange text-navy font-semibold text-sm
                           hover:bg-orange-dark transition-colors">
                I've saved my codes — Continue
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
